import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Nlp } from '@nlpjs/nlp';
import { firstValueFrom } from 'rxjs';

/** Minimal typings for the subset of the @nlpjs/nlp API we use. */
interface NlpInstance {
  addDocument(locale: string, utterance: string, intent: string): void;
  train(): Promise<void>;
  process(locale: string, utterance: string): Promise<NlpResult>;
  import(json: string): void;
}

interface NlpResult {
  intent: string;
  score: number;
}

/** Shape of assets/chatbot/corpus.json (also consumed by scripts/train-chatbot.js). */
export interface ChatbotCorpus {
  locale: string;
  data: { intent: string; utterances: string[] }[];
}

/** Minimum confidence score to accept an NLP classification result. */
const CONFIDENCE_THRESHOLD = 0.5;

/** Language-agnostic locale: the multilingual corpus is trained together. */
const NLP_LOCALE = 'xx';

const MODEL_ASSET_PATH = 'assets/chatbot/model.nlp';
const CORPUS_ASSET_PATH = 'assets/chatbot/corpus.json';

/**
 * Wraps the @nlpjs/nlp classifier.
 *
 * Loads the pre-trained model (assets/chatbot/model.nlp, built by `npm run train:chatbot`).
 * If unavailable, trains in memory from assets/chatbot/corpus.json — the single corpus source.
 *
 * Returns null on SSR, on load failure, or when confidence is below CONFIDENCE_THRESHOLD,
 * so the caller relies on regex matching only.
 */
@Injectable({ providedIn: 'root' })
export class ChatbotNlpService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly http = inject(HttpClient);

  private nlpPromise: Promise<NlpInstance | null> | null = null;

  /** Best matching intent ID + score for pre-normalised text, or null. */
  async classifyIntent(text: string): Promise<{ intent: string; score: number } | null> {
    if (!isPlatformBrowser(this.platformId)) { return null; }
    this.nlpPromise ??= this.initNlp().catch(() => null);
    const nlp = await this.nlpPromise;
    if (!nlp) { return null; }

    const result = await nlp.process(NLP_LOCALE, text);
    if (result?.intent && result.intent !== 'None' && result.score >= CONFIDENCE_THRESHOLD) {
      return { intent: result.intent, score: result.score };
    }
    return null;
  }

  private async initNlp(): Promise<NlpInstance> {
    const nlp: NlpInstance = new Nlp({
      languages: [NLP_LOCALE],
      autoSave: false,
      autoLoad: false,
      nlu: { log: false },
    });
    if (await this.tryLoadPrebuiltModel(nlp)) { return nlp; }

    const corpus = await firstValueFrom(this.http.get<ChatbotCorpus>(CORPUS_ASSET_PATH));
    for (const entry of corpus.data) {
      for (const utterance of entry.utterances) {
        nlp.addDocument(NLP_LOCALE, utterance, entry.intent);
      }
    }
    await nlp.train();
    return nlp;
  }

  private async tryLoadPrebuiltModel(nlp: NlpInstance): Promise<boolean> {
    try {
      const json = await firstValueFrom(this.http.get(MODEL_ASSET_PATH, { responseType: 'text' }));
      nlp.import(json);
      return true;
    } catch {
      return false;
    }
  }
}
